import './Home.scss';

import { Alert, SkipNav } from '@ver-uds/uswds-react';
import React, { useEffect } from 'react';

import { AccountType } from 'eve-account-client';
import { Helmet } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import { EverifyUserRole, User } from '@vis-auth/vis-user-client';
import CaseAlertDashboard from './CaseAlertDashboard/CaseAlertDashboard';
import HomeButtons from './HomeButtons/HomeButtons';
import HomeHeader from './HomeHeader/HomeHeader';
import IEDeprecationAlert from '../IEDeprecationAlert/IEDeprecationAlert';
import BasicTerminationBanner from '../BasicTerminationBanner/BasicTerminationBanner';
import AgentTerminationBanner from '../AgentTerminationBanner/AgentTerminationBanner';
import { basicTerminationBannerDisplay } from '../../utils/terminationBannerDisplay';
import { useAccount } from '../../context/Account/AccountContext';
import I9Constants from '../I9Constants/I9Constants';
import LatestNewsCard from '../LatestNewsCard/LatestNewsCard';
import { useToggles } from '../../context/Toggles/TogglesContext';
import HomeEVerifyPlusBanner from './HomeEVerifyBanner/HomeEVerifyPlusBanner';
import { useUser } from '../../context/User/UserContext';
import AccountMigrationModal from '../AccountMigrationModal/AccountMigrationModal';
import NewEVerifyPlusModal from '../NewEVerifyPlusModal/NewEVerifyPlusModal';

const Home = (): JSX.Element => {
  const toggles = useToggles();
  const account = useAccount();

  const user = useUser() as User;
  const evePlusEnabled = account.myI9?.enabled ?? false;
  const showBasicTerminationBanner = basicTerminationBannerDisplay(account);
  const i9Completed = !!sessionStorage.getItem('I9TrainingCompleted');
  const mfaEnabledToggle = toggles?.get('MFA_USER_CREATION') === 'on';
  const isMfaMandatoryEnabled = toggles?.get('MFA_MANDATORY') === 'on';
  const showMfaMigration = mfaEnabledToggle && !user.mfaEnabled;
  const isProgramAdmin = user.roles.includes(EverifyUserRole.PROGRAMADMIN);
  const isGeneralUser = user.roles.includes(EverifyUserRole.GENERAL);

  const showEvePlusModal = (): boolean => {
    if (account.isEvePlusEligible && !evePlusEnabled && isMfaMandatoryEnabled) {
      if (isGeneralUser || isProgramAdmin) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname + window.location.search,
      pageTitle: 'E-Verify - Home',
    });
    sessionStorage.removeItem('I9TrainingCompleted');
  }, []);

  return (
    <>
      <Helmet>
        <title>Home</title>
      </Helmet>
      <HomeHeader />
      <div className="home-page-container grid-container">
        <div className="grid-row">
          <div className="grid-col">
            <SkipNav.MainContent>
              <IEDeprecationAlert />
              {showBasicTerminationBanner && <BasicTerminationBanner closedDate={account.closedDate} />}
              <AgentTerminationBanner account={account} />
              {i9Completed && (
                <Alert status="success" slim className="margin-bottom-2 margin-top-2">
                  <Alert.Text>Congratulations! Your company now has access to {I9Constants.PROGRAM_NAME}.</Alert.Text>
                </Alert>
              )}
              {showMfaMigration && <AccountMigrationModal />}
              {account.isEvePlusEligible && !evePlusEnabled && <HomeEVerifyPlusBanner />}
              {showEvePlusModal() && (
                <NewEVerifyPlusModal enableButtonLabel={isProgramAdmin ? 'Enable E-Verify+' : ''} />
              )}
              {account.type !== AccountType.CORPORATE && <CaseAlertDashboard />}
              <HomeButtons
                createCaseModal={evePlusEnabled && toggles?.get('DISABLE_EVE_PLUS_CASE_CREATION') === 'off'}
              />
              <LatestNewsCard addTopMargin />
            </SkipNav.MainContent>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
